import { useEffect, useMemo, useRef, useState } from 'react';
import type { BotDifficultyKey, BotSeat, LobbyInfo, LobbyMemberInfo, LobbySummary } from '../../api/types';
import { REFRESH, useLobbyOperation, useLobbySummary } from '../../queries';
import { Badge, Button, Dialog, Select, Spinner, cx } from '../../ui';
import { useToast } from '../../ui/toast';
import {
  SLOTS_PER_SIDE,
  benchLabel,
  buildLobbyBoard,
  canKick,
  canUnseat,
  seatedPlayers,
  type BoardSlot,
} from './seating';
import { BOT_SEATS, BOT_SEAT_LABELS, describeLobbyOperation, type LobbyOperation } from './lobbyOperations';
import {
  describeMovement,
  diffMembers,
  movementAge,
  pruneMovements,
  recordMovements,
  type Movement,
} from './movement';
import { useLobbyLive } from './useLobbyLive';

const DIFFICULTIES: readonly BotDifficultyKey[] = ['passive', 'easy', 'medium', 'hard', 'unfair'];
const SLOT_NUMBERS = Array.from({ length: SLOTS_PER_SIDE }, (_, index) => index + 1);

/** How long a movement stays in the log and keeps its row highlighted. */
const MOVEMENT_TTL_MS = 12_000;

function MemberBadges({ member }: { member: LobbyMemberInfo }) {
  return (
    <>
      {member.is_bot && <Badge tone="info">bot</Badge>}
      {member.is_leader && !member.is_bot && <Badge tone="gold">leader</Badge>}
      {member.coach_team && <Badge tone="neutral">coach</Badge>}
    </>
  );
}

/** A row that just changed is outlined, so movement is visible without reading names. */
function movementClasses(movement: Movement | undefined): string | false {
  if (!movement) return false;
  return movement.kind === 'joined' ? 'bg-ok/10 ring-1 ring-ok/50' : 'bg-info/10 ring-1 ring-info/50';
}

function SlotRow({
  slot,
  side,
  series,
  busy,
  movement,
  onUnseat,
  onKick,
  onAddBot,
}: {
  slot: BoardSlot;
  side: 'radiant' | 'dire';
  series: boolean;
  busy: boolean;
  movement?: Movement;
  onUnseat: (member: LobbyMemberInfo) => void;
  onKick: (member: LobbyMemberInfo) => void;
  onAddBot: (side: 'radiant' | 'dire', slot: number) => void;
}) {
  const { member } = slot;
  return (
    <li
      className={cx(
        'flex min-h-9 items-center gap-2 rounded-md px-1 text-xs transition-colors',
        member && movementClasses(movement),
      )}
    >
      <span className="w-4 shrink-0 text-right font-mono text-ink-faint">{slot.slot}</span>
      {member ? (
        <>
          <span className="min-w-0 flex-1 truncate text-ink" title={member.steam_id}>
            {member.name}
          </span>
          <MemberBadges member={member} />
          {canUnseat(member) && (
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => onUnseat(member)}>
              Unseat
            </Button>
          )}
          {canKick(member) && (
            <Button size="sm" variant="ghost" className="text-bad" disabled={busy} onClick={() => onKick(member)}>
              Kick
            </Button>
          )}
        </>
      ) : (
        <>
          <span className="min-w-0 flex-1 text-ink-faint">Empty</span>
          {!series && (
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => onAddBot(side, slot.slot)}>
              + AI bot
            </Button>
          )}
        </>
      )}
    </li>
  );
}

function SideColumn({
  side,
  name,
  slots,
  movements,
  ...rowProps
}: {
  side: 'radiant' | 'dire';
  name: string;
  slots: BoardSlot[];
  movements: Map<string, Movement>;
  series: boolean;
  busy: boolean;
  onUnseat: (member: LobbyMemberInfo) => void;
  onKick: (member: LobbyMemberInfo) => void;
  onAddBot: (side: 'radiant' | 'dire', slot: number) => void;
}) {
  const seated = seatedPlayers(slots);
  return (
    <div className="min-w-0 flex-1 rounded-lg border border-line bg-surface-2/60 p-3">
      <p
        className={cx(
          'mb-2 flex items-center justify-between text-sm font-semibold',
          side === 'radiant' ? 'text-ok' : 'text-ember-400',
        )}
      >
        <span className="truncate">{name}</span>
        <span className="text-xs font-normal text-ink-faint">
          {seated}/{SLOTS_PER_SIDE}
        </span>
      </p>
      <ul className="divide-y divide-line/60">
        {slots.map((slot) => (
          <SlotRow
            key={slot.slot}
            slot={slot}
            side={side}
            movement={slot.member ? movements.get(slot.member.steam_id) : undefined}
            {...rowProps}
          />
        ))}
      </ul>
    </div>
  );
}

/** What an API without per-member data can still show: names per side. */
function NamesOnly({ summary }: { summary: LobbySummary }) {
  const column = (title: string, names: string[], tone: string) => (
    <div className="min-w-0 flex-1 rounded-lg border border-line bg-surface-2/60 p-3">
      <p className={cx('mb-2 truncate text-sm font-semibold', tone)}>{title}</p>
      {names.length === 0 ? (
        <p className="text-xs text-ink-faint">Empty</p>
      ) : (
        <ul className="space-y-1">
          {names.map((name, index) => (
            <li key={`${name}-${index}`} className="truncate text-xs text-ink-dim">
              {name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row">
        {column(summary.leftTeam?.name || 'Radiant', summary.leftTeam?.playerNames ?? [], 'text-ok')}
        {column(summary.rightTeam?.name || 'Dire', summary.rightTeam?.playerNames ?? [], 'text-ember-400')}
      </div>
      <p className="mt-3 text-xs text-ink-faint">
        This API reports names only. Update it to seat, unseat and kick players from here.
      </p>
    </div>
  );
}

/**
 * The seating board of a live lobby with the leader's controls: swap sides,
 * shuffle, kick or unseat a player, move the bot, place AI bots. On a series
 * lobby the shuffle and the AI bots are hidden: the orchestrator tracks the
 * two teams by who sits where, and AI bots would count as players.
 *
 * While the board is open it follows the lobby through the API's long poll, so
 * a player sitting down shows up as fast as the Game Coordinator reports it,
 * and whoever moved is called out for a few seconds.
 */
export default function LobbyBoard({ lobby, series }: { lobby: LobbyInfo; series: boolean }) {
  const toast = useToast();
  const { live, unsupported } = useLobbyLive(lobby.bot_id, lobby.lobby_id, true);
  const { data, isPending, isError, error } = useLobbySummary(lobby.lobby_id, {
    refetchInterval: live ? REFRESH.lobbySummaryLive : REFRESH.lobbySummary,
  });
  const operation = useLobbyOperation();

  const [difficulty, setDifficulty] = useState<BotDifficultyKey>('medium');
  const [seat, setSeat] = useState<BotSeat>('spectator');
  const [seatSlot, setSeatSlot] = useState(1);
  const [pendingKick, setPendingKick] = useState<LobbyMemberInfo | null>(null);
  const [movements, setMovements] = useState<Movement[]>([]);
  const [now, setNow] = useState(() => Date.now());
  const previousMembers = useRef<LobbyMemberInfo[] | undefined>(undefined);

  const members = data?.members;
  useEffect(() => {
    if (!members) return;
    const changes = diffMembers(previousMembers.current, members);
    previousMembers.current = members;
    if (changes.length === 0) return;
    const at = Date.now();
    setNow(at);
    setMovements((log) => recordMovements(log, changes, at));
  }, [members]);

  // The clock only runs while something is highlighted.
  useEffect(() => {
    if (movements.length === 0) return;
    const timer = window.setInterval(() => {
      const tick = Date.now();
      setNow(tick);
      setMovements((log) => pruneMovements(log, tick, MOVEMENT_TTL_MS));
    }, 1_000);
    return () => window.clearInterval(timer);
  }, [movements.length]);

  const board = useMemo(() => buildLobbyBoard(members), [members]);
  const movementsBySteamId = useMemo(
    () => new Map(movements.filter((entry) => entry.kind !== 'left').map((entry) => [entry.steamId, entry])),
    [movements],
  );
  const busy = operation.isPending;

  const run = (op: LobbyOperation) =>
    operation.mutate(
      { botId: lobby.bot_id, lobbyId: lobby.lobby_id, op },
      {
        onSuccess: () => toast.success(describeLobbyOperation(op)),
        onError: (mutationError) => toast.error('Lobby action failed', (mutationError as Error).message),
      },
    );

  if (isPending) {
    return (
      <div className="flex items-center gap-2 py-3 text-xs text-ink-dim">
        <Spinner className="size-3" /> Loading lobby…
      </div>
    );
  }
  if (isError) {
    return <p className="py-3 text-xs text-bad">{(error as Error).message}</p>;
  }
  if (!data.members) {
    return <NamesOnly summary={data} />;
  }

  const rowProps = {
    series,
    busy,
    onUnseat: (member: LobbyMemberInfo) => run({ kind: 'unseat', steamId: member.steam_id, name: member.name }),
    onKick: setPendingKick,
    onAddBot: (side: 'radiant' | 'dire', slot: number) => run({ kind: 'bot_slot', team: side, slot, difficulty }),
  };
  const seatNeedsSlot = seat === 'radiant' || seat === 'dire';

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" disabled={busy} onClick={() => run({ kind: 'flip' })}>
          Swap sides
        </Button>
        {!series && (
          <Button size="sm" disabled={busy} onClick={() => run({ kind: 'shuffle' })}>
            Shuffle
          </Button>
        )}
        <Badge tone={live ? 'ok' : 'neutral'}>
          <span
            aria-hidden="true"
            className={cx('size-1.5 rounded-full', live ? 'animate-pulse bg-ok' : 'bg-ink-faint')}
          />
          {live ? 'Live' : unsupported ? `Every ${REFRESH.lobbySummary / 1000}s` : 'Connecting'}
        </Badge>
        <span className="ml-auto flex flex-wrap items-center gap-2 text-xs text-ink-dim">
          Bot seat
          <Select
            aria-label="Bot seat"
            compact
            value={seat}
            onChange={(event) => setSeat(event.target.value as BotSeat)}
          >
            {BOT_SEATS.map((option) => (
              <option key={option} value={option}>
                {BOT_SEAT_LABELS[option]}
              </option>
            ))}
          </Select>
          {seatNeedsSlot && (
            <Select
              aria-label="Bot slot"
              compact
              value={seatSlot}
              onChange={(event) => setSeatSlot(Number(event.target.value))}
            >
              {SLOT_NUMBERS.map((slot) => (
                <option key={slot} value={slot}>
                  {slot}
                </option>
              ))}
            </Select>
          )}
          <Button
            size="sm"
            disabled={busy}
            onClick={() => run(seatNeedsSlot ? { kind: 'seat_bot', seat, slot: seatSlot } : { kind: 'seat_bot', seat })}
          >
            Move bot
          </Button>
        </span>
      </div>

      {movements.length > 0 && (
        <ul
          aria-live="polite"
          className="space-y-1 rounded-lg border border-info/30 bg-info/5 px-3 py-2 text-xs text-ink-dim"
        >
          {movements.map((movement) => (
            <li key={`${movement.steamId}-${movement.at}`} className="flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate text-ink">{describeMovement(movement)}</span>
              <span className="shrink-0 text-ink-faint">{movementAge(movement, now)}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
        <SideColumn
          side="radiant"
          name={data.leftTeam?.name || 'Radiant'}
          slots={board.radiant}
          movements={movementsBySteamId}
          {...rowProps}
        />
        <SideColumn
          side="dire"
          name={data.rightTeam?.name || 'Dire'}
          slots={board.dire}
          movements={movementsBySteamId}
          {...rowProps}
        />
      </div>

      <div className="rounded-lg border border-line bg-surface-2/40 p-3">
        <p className="mb-2 text-xs font-semibold tracking-wide text-ink-dim uppercase">
          Bench <span className="font-normal text-ink-faint">({board.bench.length})</span>
        </p>
        {board.bench.length === 0 ? (
          <p className="text-xs text-ink-faint">Nobody waiting</p>
        ) : (
          <ul className="divide-y divide-line/60">
            {board.bench.map((member) => (
              <li
                key={member.steam_id}
                className={cx(
                  'flex min-h-9 items-center gap-2 rounded-md px-1 text-xs transition-colors',
                  movementClasses(movementsBySteamId.get(member.steam_id)),
                )}
              >
                <span className="min-w-0 flex-1 truncate text-ink" title={member.steam_id}>
                  {member.name}
                </span>
                <Badge tone="neutral">{benchLabel(member)}</Badge>
                <MemberBadges member={member} />
                {canKick(member) && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-bad"
                    disabled={busy}
                    onClick={() => setPendingKick(member)}
                  >
                    Kick
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {!series && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-ink-dim">
          AI bots
          <Select
            aria-label="AI bot difficulty"
            compact
            value={difficulty}
            onChange={(event) => setDifficulty(event.target.value as BotDifficultyKey)}
          >
            {DIFFICULTIES.map((level) => (
              <option key={level} value={level}>
                {level}
              </option>
            ))}
          </Select>
          <Button size="sm" disabled={busy} onClick={() => run({ kind: 'fill_bots', difficulty })}>
            Fill empty slots
          </Button>
          <span className="text-ink-faint">or use + AI bot on one slot</span>
        </div>
      )}

      <Dialog
        open={pendingKick !== null}
        onClose={() => setPendingKick(null)}
        size="sm"
        title="Kick this player?"
        description="They leave the lobby and need a new invite to come back."
        footer={
          <>
            <Button variant="ghost" onClick={() => setPendingKick(null)}>
              Keep them
            </Button>
            <Button
              variant="danger"
              loading={busy}
              onClick={() => {
                if (!pendingKick) return;
                run({ kind: 'kick', steamId: pendingKick.steam_id, name: pendingKick.name });
                setPendingKick(null);
              }}
            >
              Kick
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-dim">
          {pendingKick?.name} <span className="font-mono text-xs text-ink-faint">{pendingKick?.steam_id}</span>
        </p>
      </Dialog>
    </div>
  );
}
